/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Injectable} from '@angular/core';
import * as _ from 'lodash';

import {ASSET_ERROR} from '../api/resources/resources-constants';
import {ApiConfig} from '../api/api';
import {AdnHttpProgressCallback, AdnHttpService} from './adn-http.service';
import {
  AdnHttpSender,
  AdnHttpUploadSender,
  createAdnHttpResourceSender,
  createAdnHttpUploadSender,
  createAdnUploadFormData
} from './create-adn-http-resource';
import {AuthHttpInterceptorService} from '../auth/auth-http-interceptor.service';
import {ApiErrorHandlerService} from '../../app/common/directives/error/api-error-handler.service';

@Injectable({
  providedIn: 'root'
})
export class AdnAssetHelperService {
  private readonly send: AdnHttpSender;
  private readonly sendUpload: AdnHttpUploadSender;

  constructor(
    AdnHttp: AdnHttpService,
    authHttpInterceptor: AuthHttpInterceptorService,
    ApiErrorHandler: ApiErrorHandlerService
  ) {
    this.send = createAdnHttpResourceSender(AdnHttp, authHttpInterceptor, ApiErrorHandler);
    this.sendUpload = createAdnHttpUploadSender(AdnHttp, authHttpInterceptor, ApiErrorHandler);
  }

  getAsJson(uriParams: any, qStringParams?: any): any {
    return this.send({
      method: 'GET',
      url: ApiConfig.obtain().getVersionedUri(uriParams),
      params: _.assign({}, qStringParams, {format: 'json'})
    });
  }

  fromParentAsJson(path: any, id: any, qStringParams?: any): any {
    return this.send({
      method: 'GET',
      url: ApiConfig.obtain().getVersionedUri([path, id]),
      params: _.assign({}, qStringParams, {format: 'json', concise: true, pageSize: 1000})
    });
  }

  helpfulUpload(uri: any, parentId: any, data: any, onProgress?: AdnHttpProgressCallback): any {
    return this.theUpload(uri, parentId, data, null, onProgress);
  }

  upload(uri: any, parentId?: any, data?: any, assetId?: any, onProgress?: AdnHttpProgressCallback): any {
    return this.theUpload(uri, parentId, data, assetId, onProgress);
  }

  private theUpload(uri: any, parentId: any, data: any, assetId: any, onProgress?: AdnHttpProgressCallback): any {
    if (!data || !(data.file || data.content)) {
      return Promise.reject(ASSET_ERROR.NO_FILE);
    }
    const pathPortions = _.isArray(uri) ? uri.slice() : [uri];
    if (parentId) {
      pathPortions.push(parentId);
    }
    if (assetId) {
      pathPortions.push(assetId);
    }
    if (data.transcodeConfig) {
      data = _.assign({}, data, {transcodeConfig: JSON.stringify(data.transcodeConfig)});
    }
    return this.sendUpload({
      method: 'POST',
      url: ApiConfig.obtain().getVersionedUri(pathPortions),
      body: createAdnUploadFormData(data)
    }, onProgress).then((response: any) => {
      if (_.isEmpty(response.data)) {
        return Promise.reject(ASSET_ERROR.EMPTY_RESPONSE);
      }
      // always return an array so we don't have to muck around with type detection
      return _.isArray(response.data) ? response.data : [response.data];
    });
  }
}
